import { Card } from "@/components/ui";
import { cn } from "@/lib/utils/cn";

/**
 * The snapshot filter picker.
 *
 * This used to be a tab of its own, holding three settings — enabled, filters,
 * retakes — one of which (enabled) was *also* a toggle in General, so the two
 * screens could disagree about whether Snapshot was on. It now lives inside the
 * Flow tab, where the rest of the guest journey is, and this file is just the
 * filter grid.
 */

/** Mirrors SnapshotFilters::LIBRARY on the server — same slugs, same CSS. */
const FILTER_LIBRARY = [
  { slug: "none", name: "Original", css: "none" },
  { slug: "mono", name: "Mono", css: "grayscale(1) contrast(1.08)" },
  { slug: "film", name: "Film", css: "sepia(0.28) contrast(1.12) saturate(0.9) brightness(1.02)" },
  { slug: "warm", name: "Warm", css: "saturate(1.25) sepia(0.15) brightness(1.05)" },
  { slug: "cool", name: "Cool", css: "saturate(1.1) hue-rotate(-12deg) brightness(1.03)" },
  { slug: "vivid", name: "Vivid", css: "saturate(1.6) contrast(1.15)" },
  { slug: "fade", name: "Fade", css: "saturate(0.75) contrast(0.92) brightness(1.08)" },
  { slug: "noir", name: "Noir", css: "grayscale(1) contrast(1.45) brightness(0.92)" },
  { slug: "bloom", name: "Bloom", css: "saturate(1.2) brightness(1.12) contrast(0.95)" },
];

// A neutral face-ish gradient so filter swatches show a real difference
// without shipping a photo asset.
const SWATCH =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120">
      <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#f2c9a0"/><stop offset="55%" stop-color="#b5703f"/>
        <stop offset="100%" stop-color="#2a1a12"/></linearGradient></defs>
      <rect width="120" height="120" fill="url(#g)"/>
      <circle cx="60" cy="48" r="26" fill="rgba(255,255,255,0.22)"/>
    </svg>`,
  );

export function SnapshotFilters({
  selected, onChange,
}: {
  selected: string[];
  onChange: (filters: string[]) => void;
}) {
  function toggle(slug: string) {
    const next = selected.includes(slug)
      ? selected.filter((item) => item !== slug)
      : [...selected, slug];

    // An empty filter list would leave the booth with no buttons at all.
    onChange(next.length > 0 ? next : ["none"]);
  }

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
        {FILTER_LIBRARY.map((filter) => {
          const active = selected.includes(filter.slug);
          return (
            <button
              key={filter.slug}
              onClick={() => toggle(filter.slug)}
              className={cn(
                "group flex flex-col items-center gap-2 rounded-xl border p-2 transition-colors",
                active ? "border-accent bg-accent/[0.06]" : "border-ink-600 hover:border-ink-500",
              )}
            >
              <span className="relative block h-16 w-full overflow-hidden rounded-lg">
                <img
                  src={SWATCH}
                  alt=""
                  className="h-full w-full object-cover"
                  style={{ filter: filter.css === "none" ? undefined : filter.css }}
                />
                {active && (
                  <span className="absolute right-1 top-1 grid h-4 w-4 place-items-center rounded-full bg-accent text-[9px] font-bold text-accent-ink">
                    ✓
                  </span>
                )}
              </span>
              <span className={cn("text-2xs", active ? "text-accent" : "text-haze-400")}>
                {filter.name}
              </span>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-haze-400">
        {selected.length} filter{selected.length === 1 ? "" : "s"} enabled. Guests see them in
        this order.
      </p>
    </Card>
  );
}
